import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import styled from 'styled-components';
import { FiShield, FiCheckCircle, FiLock, FiInfo } from 'react-icons/fi';

const StyledModal = styled(Modal)`
  .modal-content {
    background:
      radial-gradient(circle at 12% 0%, rgba(117, 180, 245, 0.16), transparent 30%),
      linear-gradient(160deg, #fcfeff, #f1f7fd);
    color: #202630;
    border: 1px solid rgba(117, 154, 198, 0.22);
    border-radius: 29px;
    overflow: hidden;
    box-shadow: 0 38px 90px rgba(10, 31, 58, 0.28), inset 0 1px 0 #fff;
  }
  .modal-header {
    border: none;
    padding: 2rem 2rem 0.8rem;
    position: relative;
    background: transparent;
    
    .btn-close {
      position: absolute;
      top: 1.5rem;
      right: 1.5rem;
      filter: none;
      opacity: 0.62;
      z-index: 10;
    }
  }
  .modal-body {
    padding: 1rem 2rem 2rem;
    max-height: 70vh;
    overflow-y: auto;
    
    /* Custom Scrollbar */
    &::-webkit-scrollbar { width: 6px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: #aeb9c5; border-radius: 10px; }
    &::-webkit-scrollbar-thumb:hover { background: #8896a5; }
  }
  .modal-footer {
    border-top: 1px solid rgba(17, 19, 24, 0.08);
    padding: 1.15rem 2rem;
    background: rgba(255, 255, 255, 0.55);
  }

  .modal-body > p {
    color: #697582;
    font-size: 0.93rem;
    line-height: 1.72;
  }

  @media (max-width: 576px) {
    .modal-dialog { margin: 8px; }
    .modal-content { border-radius: 23px; }
    .modal-header { padding: 1.5rem 1.25rem 0.7rem; }
    .modal-body { padding: 0.8rem 1.25rem 1.5rem; }
    .modal-footer { padding: 1rem 1.25rem; }
    .modal-header .btn-close { top: 1.1rem; right: 1rem; }
  }
`;

const SectionBox = styled.div`
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(75, 114, 160, 0.12);
  border-radius: 19px;
  padding: 1.35rem;
  margin-bottom: 0.85rem;
  box-shadow: inset 0 1px 0 #fff;
  
  h3 {
    font-size: 1.1rem;
    font-weight: 800;
    color: #326fae;
    margin-bottom: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  
  p, li {
    font-size: 0.95rem;
    color: #5f6d7b;
    line-height: 1.7;
    margin-bottom: 0.75rem;
    word-break: keep-all;
  }
  
  strong {
    color: #253242;
    font-weight: 700;
  }

  ul {
    padding-left: 1.25rem;
    margin-bottom: 0;
    li {
        list-style: none;
        position: relative;
        padding-left: 0.5rem;
        &::before {
            content: '•';
            position: absolute;
            left: -1rem;
            color: #397bcf;
        }
    }
  }
`;

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  .badge {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    width: fit-content;
    padding: 0.4rem 0.8rem;
    background: #e8f3fd;
    border: 1px solid rgba(57, 123, 207, 0.16);
    border-radius: 999px;
    color: #397bcf;
    font-size: 0.75rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  h2 {
    font-size: 2rem;
    font-weight: 900;
    color: #171b22;
    margin: 0;
  }
`;

const PrivacyModal = ({ show, onHide }) => {
  return (
    <StyledModal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <HeaderTitle>
            <div className="badge"><FiShield /> Data Protection</div>
            <h2>개인정보 처리방침</h2>
        </HeaderTitle>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-5 text-slate-400 font-medium leading-relaxed">
            나니아랩은 고객님의 소중한 정보를 안전하게 보호하기 위해 최선을 다하고 있습니다. <br />
            본 방침은 관련 법령에 의거하여 개인정보가 어떤 용도와 방식으로 이용되고 있는지 안내해 드립니다.
        </p>

        <SectionBox>
            <h3><FiInfo /> 제1조 (개인정보의 처리 목적)</h3>
            <p>회사는 다음의 목적을 위해 필요한 최소한의 개인정보를 처리하며, 목적 외의 용도로는 이용하지 않습니다.</p>
            <ul>
                <li><strong>서비스 회원가입 및 관리</strong>: 회원 가입의사 확인, 학생 및 교사 본인 식별·인증, 회원자격 유지·관리.</li>
                <li><strong>AI 교육 서비스 제공</strong>: 맞춤형 프로젝트 기반 학습 콘텐츠 제공, AI-Helper를 통한 실시간 학습 지원.</li>
                <li><strong>안전한 교육 환경 조성</strong>: 만 14세 미만 아동 보호를 위한 법정대리인 동의 확인.</li>
            </ul>
        </SectionBox>

        <SectionBox>
            <h3><FiLock /> 제2조 (처리하는 개인정보의 항목)</h3>
            <ul>
                <li><strong>필수 수집 항목</strong>: 성명, 아이디, 비밀번호, 이메일 주소, 연락처(휴대폰 번호).</li>
                <li><strong>수집 방법</strong>: 공식 홈페이지 회원가입 및 서비스 이용 과정에서의 자발적 입력.</li>
            </ul>
        </SectionBox>

        <SectionBox>
            <h3><FiCheckCircle /> 제3조 (보유 및 이용 기간)</h3>
            <p>회사는 원칙적으로 개인정보 수집 및 이용목적이 달성된 후에는 해당 정보를 지체 없이 파기합니다.</p>
            <ul>
                <li><strong>홈페이지 회원 가입 정보</strong>: 회원 탈퇴 시까지</li>
                <li><strong>관련 법령에 의한 보존</strong>: 계약/청약철회(5년), 소비자 불만/분쟁처리(3년)</li>
            </ul>
        </SectionBox>

        <SectionBox>
            <h3><FiShield /> 제4조 (안전성 확보 조치)</h3>
            <p>회사는 해킹이나 컴퓨터 바이러스 등에 의한 개인정보 유출 및 훼손을 막기 위하여 다음과 같은 기술적/관리적 대책을 강구하고 있습니다.</p>
            <ul>
                <li>개인정보의 암호화 저장 및 전송 구간 보안(SSL) 적용</li>
                <li>접근 권한의 단계적 제한 및 정기적인 내부 보안 감사</li>
            </ul>
        </SectionBox>

        <p className="text-center text-slate-500 text-sm mt-4">
            최종 업데이트: 2025년 12월 3일 | <strong>나니아랩 (NARNIA LAB)</strong>
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button 
            variant="primary" 
            style={{ 
                borderRadius: '13px',
                fontWeight: 760,
                padding: '0.75rem 2.5rem',
                background: 'linear-gradient(135deg, #183f6d, #347bc0)',
                border: 'none',
                boxShadow: '0 12px 26px rgba(35, 91, 149, 0.22)'
            }} 
            onClick={onHide}
        >
            확인 및 닫기
        </Button>
      </Modal.Footer>
    </StyledModal>
  );
};

export default PrivacyModal;
